import React, { useCallback, useMemo } from 'react';
import type { RenderColumnDataProps } from 'c-pagination';
import type { Campaign } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { AutoGrid, Button, Checkbox } from 'c-components';
import { useCommonTranslation } from 'c-translation';
import { Description } from '@mui/icons-material';
import UpdatedByUserCaption from 'c-components/UpdatedByUserCaption';
import { getCampaignBookingSummaries } from 'c-main/Lib';
import { useBoolean } from 'react-hanger';
import { useUpdateEntityData } from 'c-data';
import { NetworkRequestState } from 'c-data-layer';
import { useUserPermissions } from 'c-auth-module/Hooks';
import OutboxIcon from '@mui/icons-material/Outbox';
import CampaignBookingSummaryDialog from '../CampaignBookingSummary/CampaignBookingSummaryDialog';
import DataStatusSendReportDialog from './DataStatusSendReportDialog';

import CampaignUserEditedBooleanCheckbox from './CampaignUserEditedBooleanCheckbox';
import {
    bookingSummaryValidatedToggle,
    inflightReportSentUpdate,
    isLinearCampaignType,
    pcaReportRecipients,
    pcaReportSentUpdate,
    signOffUpdate,
} from './signOff';

type Props = RenderColumnDataProps<Campaign>;

const CampaignSignOffCell: React.FC<Props> = ({ entity }) => {
    const count = useMemo(() => getCampaignBookingSummaries(entity).length, [entity]);
    const dialogState = useBoolean(false);
    const pcaDialogState = useBoolean(false);
    const inflightDialogState = useBoolean(false);

    const { update, getUpdatingById } = useUpdateEntityData<Campaign>('Campaign');
    const onPcaSendSuccess = useCallback(() => {
        if (entity.data_status.pca_report_sent) return;
        update(entity.id, pcaReportSentUpdate(entity, true));
    }, [entity, update]);
    const onInFlightSendSuccess = useCallback(() => {
        if (entity.data_status.inflight_report_sent) return;
        update(entity.id, inflightReportSentUpdate(entity, true));
    }, [entity, update]);
    const loading = useMemo(
        () => getUpdatingById(entity.id).state === NetworkRequestState.InProgress,
        [entity.id, getUpdatingById],
    );

    const onChange = useCallback(() => {
        update(entity.id, bookingSummaryValidatedToggle(entity));
    }, [update, entity]);

    const { hasAdminPermission, hasAll } = useUserPermissions();
    const canSendReport = useMemo(
        () =>
            hasAll([PermissionName.PdfPcaReportRead]) ||
            hasAll([PermissionName.PowerpointPcareportRead]),
        [hasAll],
    );
    const isLinear = useMemo(() => isLinearCampaignType(entity.type), [entity.type]);
    const initiatedEmails = useMemo(
        () =>
            pcaReportRecipients({
                additionalContacts: entity.additionalContacts,
                user: entity.user,
            }),
        [entity.additionalContacts, entity.user],
    );
    return (
        <Stack gap={1}>
            <AutoGrid columnGap={0} flexWrap="nowrap" alignItems="center">
                <Tooltip
                    title={useCommonTranslation(
                        'Modules.Main.Campaigns.DataStatus.table.bookingSummaryValidated',
                    )}
                >
                    <Box>
                        <Checkbox
                            disabled={loading || !hasAdminPermission}
                            checked={entity.booking_summary_validated}
                            label={
                                <Typography variant="caption" noWrap>
                                    {useCommonTranslation(
                                        'Modules.Main.Campaigns.DataStatus.table.bookingSummaryCol',
                                    )}
                                </Typography>
                            }
                            onChange={onChange}
                            isBoolean
                            checkedIcon={loading ? <CircularProgress size={15} /> : undefined}
                            icon={loading ? <CircularProgress size={15} /> : undefined}
                            formControlLabelSx={{ mr: 0 }}
                        />
                    </Box>
                </Tooltip>
                <Button
                    variant="text"
                    sx={{ whiteSpace: 'nowrap' }}
                    size="small"
                    onClick={dialogState.setTrue}
                >
                    <Description fontSize="inherit" sx={{ mr: 0.5 }} />
                    {useCommonTranslation(
                        'Modules.Main.Campaigns.DataStatus.table.bookingSummaryFileCount',
                        { count },
                    )}
                </Button>
            </AutoGrid>
            <CampaignUserEditedBooleanCheckbox
                campaign={entity}
                value={entity.data_status.inflight_report_sent}
                dateKey="inflight_report_requested_at"
                userIdKey="inflight_report_requested_by"
                label="Modules.Main.Campaigns.DataStatus.table.inflightReportSentCheckboxLabel"
                generateSaveData={inflightReportSentUpdate}
                action={
                    canSendReport ? (
                        <IconButton onClick={inflightDialogState.setTrue}>
                            <OutboxIcon />
                        </IconButton>
                    ) : null
                }
            />
            <CampaignUserEditedBooleanCheckbox
                campaign={entity}
                value={entity.sign_off}
                dateKey="sign_off_updated_at"
                userIdKey="sign_off_updated_by"
                label="Modules.Main.Campaigns.DataStatus.table.signOffCheckboxLabel"
                generateSaveData={signOffUpdate}
            />
            <CampaignUserEditedBooleanCheckbox
                campaign={entity}
                value={entity.data_status.pca_report_sent}
                dateKey="pca_report_requested_at"
                userIdKey="pca_report_requested_by"
                label="Modules.Main.Campaigns.DataStatus.table.pcaReportSentCheckboxLabel"
                generateSaveData={pcaReportSentUpdate}
                action={
                    canSendReport ? (
                        <IconButton onClick={pcaDialogState.setTrue}>
                            <OutboxIcon />
                        </IconButton>
                    ) : null
                }
            />
            <CampaignBookingSummaryDialog
                open={dialogState.value}
                onClose={dialogState.setFalse}
                campaign={entity}
            />
            <DataStatusSendReportDialog
                id={entity.id}
                pcaState={pcaDialogState}
                isLinear={isLinear}
                campaignStartDate={entity?.date_start ?? null}
                campaignEndDate={entity?.date_end ?? null}
                reportTypeToSend="pca"
                dialogTitle="PCA Report"
                onSendSuccess={onPcaSendSuccess}
                initiatedEmails={initiatedEmails}
            />
            <DataStatusSendReportDialog
                id={entity.id}
                pcaState={inflightDialogState}
                isLinear={isLinear}
                campaignStartDate={entity?.date_start ?? null}
                campaignEndDate={entity?.date_end ?? null}
                reportTypeToSend="inflight"
                dialogTitle="PCA Inflight Report"
                onSendSuccess={onInFlightSendSuccess}
                initiatedEmails={initiatedEmails}
            />

            {entity?.booking_summary_validated_updated_by != null && (
                <UpdatedByUserCaption
                    userId={entity.booking_summary_validated_updated_by}
                    updatedAt={entity.booking_summary_validated_updated_at}
                />
            )}
        </Stack>
    );
};

export default CampaignSignOffCell;
